import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Pause, Play, ChevronLeft, ChevronRight } from 'lucide-react';
import { SLIDES } from './Slides';
import { WrappedStats } from './types';
import { useReducedMotion } from './hooks';
import './wrapped.css';

interface Props {
  stats: WrappedStats;
}

const WrappedExperience: React.FC<Props> = ({ stats }) => {
  const navigate = useNavigate();
  const reduced = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef<number | null>(null);

  const total = SLIDES.length;
  const isLast = index === total - 1;
  const current = SLIDES[index];

  const goNext = useCallback(() => setIndex((i) => Math.min(total - 1, i + 1)), [total]);
  const goPrev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  const close = useCallback(() => navigate('/'), [navigate]);

  // Autoavance: cuando termina la animación de la barra activa.
  const autoAdvances = !reduced && current.duration > 0 && !isLast;
  const handleFillEnd = (e: React.AnimationEvent) => {
    if (e.animationName !== 'wFill') return;
    if (!autoAdvances || paused) return;
    goNext();
  };

  // Teclado
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'Escape') close();
      else if (e.key === ' ') {
        e.preventDefault();
        setPaused((p) => !p);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, close]);

  // Bloquea el scroll del body mientras la experiencia está abierta.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const onTouchStart = (e: React.TouchEvent) => {
    touchX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) > 50) {
      if (dx < 0) goNext();
      else goPrev();
    }
  };

  return (
    <div className="wrapped-root" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {/* Barra de progreso */}
      <div className="wrapped-progress">
        {SLIDES.map((s, i) => {
          const state = i < index ? 'done' : i === index ? 'active' : 'upcoming';
          return (
            <div
              key={s.key}
              className={`wrapped-progress-seg ${state}${state === 'active' && paused ? ' paused' : ''}`}
            >
              <div
                className="wrapped-progress-fill"
                style={state === 'active' ? { ['--seg-duration' as any]: `${s.duration || 6000}ms` } : undefined}
                onAnimationEnd={state === 'active' ? handleFillEnd : undefined}
              />
            </div>
          );
        })}
      </div>

      {/* Barra superior: marca + controles */}
      <div className="wrapped-topbar">
        <span className="wrapped-brand">
          <span className="wrapped-brand-dot" />
          Candidaturas {stats.edition}
        </span>
        <div className="wrapped-ctrl-group">
          {!reduced && (
            <button
              className="wrapped-iconbtn"
              onClick={() => setPaused((p) => !p)}
              aria-label={paused ? 'Reanudar' : 'Pausar'}
              type="button"
            >
              {paused ? <Play size={17} /> : <Pause size={17} />}
            </button>
          )}
          <button className="wrapped-iconbtn" onClick={close} aria-label="Cerrar" type="button">
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Zonas de tap para navegar */}
      <button className="wrapped-navzone prev" onClick={goPrev} aria-label="Anterior" tabIndex={-1} />
      <button className="wrapped-navzone next" onClick={goNext} aria-label="Siguiente" tabIndex={-1} />

      {/* Flechas visibles en desktop */}
      <button className="wrapped-arrow left" onClick={goPrev} disabled={index === 0} aria-label="Anterior" type="button">
        <ChevronLeft size={22} />
      </button>
      <button className="wrapped-arrow right" onClick={goNext} disabled={isLast} aria-label="Siguiente" type="button">
        <ChevronRight size={22} />
      </button>

      {/* Anuncio del slide activo para lectores de pantalla */}
      <div className="wrapped-sr-only" aria-live="polite" aria-atomic="true">
        {`${current.title} — diapositiva ${index + 1} de ${total}`}
      </div>

      {/* Slides */}
      <div
        className="wrapped-stage"
        role="region"
        aria-roledescription="carrusel"
        aria-label={`Resumen de candidaturas ${stats.edition}`}
      >
        {SLIDES.map((s, i) => {
          const Comp = s.Comp;
          return (
            <section key={s.key} className={`wrapped-slide${i === index ? ' is-active' : ''}`} aria-hidden={i !== index}>
              <Comp stats={stats} active={i === index} />
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default WrappedExperience;
